import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RTLContextType {
  isRTL: boolean;
  direction: 'ltr' | 'rtl';
}

const RTLContext = createContext<RTLContextType>({
  isRTL: false,
  direction: 'ltr',
});

export const useRTL = () => useContext(RTLContext);

interface RTLProviderProps {
  children: React.ReactNode;
}

export const RTLProvider: React.FC<RTLProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [isRTL, setIsRTL] = useState(false);
  const [direction, setDirection] = useState<'ltr' | 'rtl'>('ltr');

  // RTL languages
  const rtlLanguages = React.useMemo(() => ['he', 'ar', 'fa', 'ur'], []);

  useEffect(() => {
    const currentLanguage = i18n.language;
    const isCurrentRTL = rtlLanguages.includes(currentLanguage);

    setIsRTL(isCurrentRTL);
    setDirection(isCurrentRTL ? 'rtl' : 'ltr');

    // Update document direction and language
    document.dir = isCurrentRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;

    // Update body attributes for language-specific styling
    document.body.setAttribute('lang', currentLanguage);
    document.body.classList.toggle('rtl', isCurrentRTL);

    // Remove old language classes and add new one
    document.body.className = document.body.className.replace(/lang-\w+/g, '');
    document.body.classList.add(`lang-${currentLanguage}`);
  }, [i18n.language, rtlLanguages]);

  return <RTLContext.Provider value={{ isRTL, direction }}>{children}</RTLContext.Provider>;
};
